import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Avatar } from 'antd';
import {
    RobotOutlined,
    CloseOutlined,
    MessageOutlined,
} from '@ant-design/icons';

const DraggableChatIcon = ({ isOpen, onToggle, onPositionChange }) => {
    const [position, setPosition] = useState({ x: window.innerWidth - 120, y: window.innerHeight - 120 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [clickStart, setClickStart] = useState(null);
    const iconRef = useRef(null);
    
    const positionRef = useRef(position);
    const isDraggingRef = useRef(isDragging);
    const dragStartRef = useRef(dragStart);
    const clickStartRef = useRef(clickStart);

    useEffect(() => {
        positionRef.current = position;
    }, [position]);

    useEffect(() => {
        isDraggingRef.current = isDragging;
    }, [isDragging]);

    useEffect(() => {
        dragStartRef.current = dragStart;
    }, [dragStart]);

    useEffect(() => {
        clickStartRef.current = clickStart;
    }, [clickStart]);

    useEffect(() => {
        const savedPosition = localStorage.getItem('aiChatIconPosition');
        if (savedPosition) {
            const parsed = JSON.parse(savedPosition);
            setPosition(parsed);
        } else {
            const defaultPos = { 
                x: window.innerWidth - 120, 
                y: window.innerHeight - 120 
            };
            setPosition(defaultPos);
        }
    }, []);

    const savePositionDebounced = useCallback(
        (() => {
            let timeoutId;
            return (newPosition) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    localStorage.setItem('aiChatIconPosition', JSON.stringify(newPosition));
                    if (onPositionChange) {
                        onPositionChange(newPosition);
                    }
                }, 100); 
            };
        })(),
        [onPositionChange]
    );

    const constrainPosition = useCallback((pos) => {
        const iconSize = 70;
        const margin = 10;
        
        return {
            x: Math.max(margin, Math.min(window.innerWidth - iconSize - margin, pos.x)),
            y: Math.max(margin, Math.min(window.innerHeight - iconSize - margin, pos.y))
        };
    }, []);

    const handleStart = useCallback((clientX, clientY) => {
        if (isOpen) return;
        
        setIsDragging(true);
        setClickStart({ x: clientX, y: clientY });
        setDragStart({
            x: clientX - positionRef.current.x,
            y: clientY - positionRef.current.y
        });
        
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';
    }, [isOpen]);

    const handleMove = useCallback((clientX, clientY) => {
        if (!isDraggingRef.current || isOpen) return;

        requestAnimationFrame(() => {
            const newPosition = constrainPosition({
                x: clientX - dragStartRef.current.x,
                y: clientY - dragStartRef.current.y
            });

            setPosition(newPosition);
        });
    }, [isOpen, constrainPosition]);

    const handleEnd = useCallback((clientX, clientY) => {
        if (!isDraggingRef.current || isOpen) return;

        setIsDragging(false);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        
        const clickThreshold = 5;
        const moved = clickStartRef.current && (
            Math.abs(clientX - clickStartRef.current.x) > clickThreshold ||
            Math.abs(clientY - clickStartRef.current.y) > clickThreshold
        );

        if (!moved && clickStartRef.current) {
            onToggle();
        }

        savePositionDebounced(positionRef.current);
        setClickStart(null);
    }, [isOpen, onToggle, savePositionDebounced]);

    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        handleStart(e.clientX, e.clientY);
    }, [handleStart]);

    const handleMouseMove = useCallback((e) => {
        e.preventDefault();
        handleMove(e.clientX, e.clientY);
    }, [handleMove]);

    const handleMouseUp = useCallback((e) => {
        handleEnd(e.clientX, e.clientY);
    }, [handleEnd]);

    // Touch event handlers - using useCallback to prevent recreation
    const handleTouchStart = useCallback((e) => {
        e.preventDefault();
        const touch = e.touches[0];
        handleStart(touch.clientX, touch.clientY);
    }, [handleStart]);

    const handleTouchMove = useCallback((e) => {
        e.preventDefault();
        const touch = e.touches[0];
        handleMove(touch.clientX, touch.clientY);
    }, [handleMove]);

    const handleTouchEnd = useCallback((e) => {
        e.preventDefault();
        const touch = e.changedTouches[0];
        handleEnd(touch.clientX, touch.clientY);
    }, [handleEnd]);

    useEffect(() => {
        if (isDragging) {
            const options = { passive: false };
            
            document.addEventListener('mousemove', handleMouseMove, options);
            document.addEventListener('mouseup', handleMouseUp);
            document.addEventListener('touchmove', handleTouchMove, options);
            document.addEventListener('touchend', handleTouchEnd);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove, options);
                document.removeEventListener('mouseup', handleMouseUp);
                document.removeEventListener('touchmove', handleTouchMove, options);
                document.removeEventListener('touchend', handleTouchEnd);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

    useEffect(() => {
        let resizeTimeout;
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                const constrainedPos = constrainPosition(positionRef.current);
                if (constrainedPos.x !== positionRef.current.x || constrainedPos.y !== positionRef.current.y) {
                    setPosition(constrainedPos);
                    savePositionDebounced(constrainedPos);
                }
            }, 150); 
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(resizeTimeout);
        };
    }, [constrainPosition, savePositionDebounced]);

    return (
        <div
            ref={iconRef}
            className={`fixed z-[9999] cursor-pointer group transition-transform duration-75 ${
                isDragging ? 'scale-110' : 'hover:scale-105'
            } ${isOpen ? "hidden md:flex" : "block"}`}
            style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
                touchAction: 'none',
                willChange: isDragging ? 'transform, left, top' : 'transform', 
                transform: isDragging ? 'translateZ(0)' : 'none'
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
        >
            <div className="relative">
                {isDragging && (
                    <div className="absolute inset-0 bg-primary rounded-full animate-ping opacity-30 scale-150"></div>
                )}
                
                {isDragging && (
                    <div className="absolute -inset-4 border border-dashed border-primary/50 rounded-full"></div>
                )}

                 {!isOpen && (
                <div className={`relative bg-gradient-to-r from-primary to-blue-600 hover:from-blue-600 hover:to-primary text-white rounded-full shadow-xl transition-shadow duration-200 ${
                    isDragging 
                        ? 'shadow-primary/70' 
                        : 'hover:shadow-primary/50'
                } ${isOpen ? 'p-3' : 'p-4'}`}>
                    
                    <div className="flex items-center justify-center">
                       
<MessageOutlined className="text-3xl" style={{ transform: "scaleX(-1)" }} />                       
                       
                    </div>

                    <div className={`absolute -top-1 -right-1 w-4 h-4 ${
                        navigator.onLine ? 'bg-green-400' : 'bg-red-400'
                    } rounded-full border-2 border-white shadow-md`}>
                    </div>
                </div>)}

                {!isDragging && !isOpen && (
                    <div className={`absolute ${
                        position.x > window.innerWidth / 2 ? 'right-full mr-3' : 'left-full ml-3'
                    } top-1/2 transform -translate-y-1/2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap transition-opacity duration-200 opacity-0 group-hover:opacity-100 shadow-lg pointer-events-none`}>
                        AI Assistant - Drag to move
                        
                        <div className={`absolute top-1/2 transform -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45 ${
                            position.x > window.innerWidth / 2 ? 'right-[-4px]' : 'left-[-4px]'
                        }`}></div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DraggableChatIcon;